import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../api';

const ChatComponent = ({ predictionId, propertyId, currentRent }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [error, setError] = useState(null);
  // Prior conversations for this listing (loaded behind a toggle to avoid clutter/perf)
  const [previousMessages, setPreviousMessages] = useState([]);
  const [showPrevious, setShowPrevious] = useState(false);
  const messagesEndRef = useRef(null);

  // Dragging state
  const [position, setPosition] = useState(null); // null means auto-position next to button
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const chatRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat session when component opens
  const startChatSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
    console.log("predictionId type:", typeof predictionId, "value:", predictionId);
    console.log("propertyId type:", typeof propertyId, "value:", propertyId);
      const response = await authFetch('/chat/start-session', {
        method: 'POST',
        body: JSON.stringify({
          prediction_id: predictionId,
          listing_id: propertyId
        })
      });

      setSessionId(response.session_id);

      // Load prior conversations for this listing (excluding the just-started session).
      try {
        const prev = await authFetch(`/chat/listing/${propertyId}/previous?exclude_session=${response.session_id}`);
        setPreviousMessages(prev?.messages || []);
      } catch (e) { /* non-fatal — just skip the previous-chats panel */ }

      setMessages([{
        id: 0,
        type: 'ai_response',
        content: "Hello, I am your AI prediction agent! First, yes, I am a robot! 🤖\n\nSecond, as you can see, I have given my prediction based on data and past feedback. However, if you think I'm off or want to add more info to help me learn to be better - \"Help me help you!\" (Did you get that Jerry Maguire reference??)\n\nJust type your question or feedback below — for example:",
        timestamp: new Date().toISOString()
      }]);
      
    } catch (err) {
      setError('Failed to start chat session');
      console.error('Chat session error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Send message to AI
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !sessionId) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user_question',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/chat/${sessionId}/message`, {
        method: 'POST',
        body: JSON.stringify({
          message: inputMessage.trim()
        })
      });

      const aiMessage = {
        id: messages.length + 2,
        type: 'ai_response',
        content: response.ai_response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (err) {
      setError('Failed to send message');
      console.error('Send message error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Calculate position next to button - moved to openChat function for synchronous execution

  // Handle dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.chat-header-drag-area')) {
      setIsDragging(true);
      const chatRect = chatRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - chatRect.left,
        y: e.clientY - chatRect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && chatRef.current) {
      const newLeft = e.clientX - dragOffset.x;
      const newTop = e.clientY - dragOffset.y;

      // Keep within viewport bounds
      const maxLeft = window.innerWidth - chatRef.current.offsetWidth;
      const maxTop = window.innerHeight - chatRef.current.offsetHeight;

      setPosition({
        left: Math.max(0, Math.min(newLeft, maxLeft)),
        top: Math.max(0, Math.min(newTop, maxTop)),
        right: 'auto',
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset]);

  // Open chat and start session
  const openChat = () => {
    // Calculate position next to button before opening
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      // Position chat below and aligned with the button
      setPosition({
        top: buttonRect.bottom + 10,
        right: window.innerWidth - buttonRect.right,
      });
    }

    setIsChatOpen(true);
    if (!sessionId) {
      startChatSession();
    }
  };

  // Close chat
  const closeChat = () => {
    setIsChatOpen(false);
    setPosition(null); // Reset position when closing
  };

  // Styles
  const chatButtonStyle = {
    backgroundColor: '#000',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease',
  };

  const chatContainerStyle = {
    position: 'fixed',
    ...(position || { bottom: '20px', right: '20px' }),
    width: '400px',
    height: '500px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };

  const chatHeaderStyle = {
    backgroundColor: '#000',
    color: 'white',
    padding: '15px 20px',
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move',
    userSelect: 'none',
  };

  const messagesContainerStyle = {
    flex: 1,
    padding: '15px',
    overflowY: 'auto',
    backgroundColor: '#f8f9fa',
  };

  const messageStyle = (isUser) => ({
    marginBottom: '12px',
    padding: '12px 16px',
    borderRadius: '18px',
    maxWidth: '85%',
    wordWrap: 'break-word',
    backgroundColor: isUser ? '#000' : 'white',
    color: isUser ? 'white' : '#333',
    marginLeft: isUser ? 'auto' : '0',
    marginRight: isUser ? '0' : 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    fontSize: '14px',
    lineHeight: '1.4',
  });

  const inputContainerStyle = {
    padding: '15px',
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
  };

  const inputStyle = {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '20px',
    fontSize: '14px',
    resize: 'vertical',
    outline: 'none',
    minHeight: '44px',
    maxHeight: '120px',
    lineHeight: '1.4',
  };

  const sendButtonStyle = {
    backgroundColor: '#000',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.6 : 1,
    transition: 'all 0.3s ease',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  };

  const errorStyle = {
    color: '#dc3545',
    fontSize: '12px',
    padding: '8px 16px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    margin: '0 15px 10px 15px',
  };

  const loadingDotsStyle = {
    display: 'flex',
    gap: '4px',
    padding: '12px 16px',
  };

  const dotStyle = {
    width: '6px',
    height: '6px',
    backgroundColor: '#666',
    borderRadius: '50%',
    animation: 'bounce 1.4s infinite ease-in-out',
  };

  const hintBoxStyle = {
    marginTop: '10px',
    padding: '10px 12px',
    backgroundColor: '#f7f9fc',
    border: '1px solid #e3e8ef',
    borderRadius: '10px',
    fontSize: '12px',
    lineHeight: '1.7',
    color: '#555',
  };

  if (!isChatOpen) {
    return (
      <button
        ref={buttonRef}
        style={chatButtonStyle}
        onClick={openChat}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#333';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#000';
        }}
      >
        💬 Ask AI
      </button>
    );
  }

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        .chat-dot:nth-child(1) { animation-delay: -0.32s; }
        .chat-dot:nth-child(2) { animation-delay: -0.16s; }
      `}</style>
      
      <div ref={chatRef} style={chatContainerStyle}>
        {/* Chat Header - Draggable */}
        <div
          className="chat-header-drag-area"
          style={chatHeaderStyle}
          onMouseDown={handleMouseDown}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              AI Property Assistant
            </h3>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
              Ask about your rent prediction
            </p>
          </div>
          <button
            style={closeButtonStyle}
            onClick={closeChat}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Messages Container */}
        <div style={messagesContainerStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          {/* Prior conversations on this listing — collapsed behind a toggle */}
          {previousMessages.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => setShowPrevious(!showPrevious)}
                style={{ fontSize: 12, padding: "4px 10px", border: "1px solid #ddd", borderRadius: 12,
                         background: "#f5f5f5", color: "#555", cursor: "pointer", width: "100%" }}>
                {showPrevious ? "▾ Hide" : "↩ Show"} previous conversations ({previousMessages.length} message{previousMessages.length !== 1 ? "s" : ""})
              </button>
              {showPrevious && (
                <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: "2px solid #e0e0e0", opacity: 0.85 }}>
                  {previousMessages.map((m) => {
                    const isUser = m.type === "user_question" || m.type === "user_message";
                    return (
                      <div key={`prev-${m.id}`} style={{ fontSize: 12, lineHeight: 1.4, margin: "5px 0", color: "#444" }}>
                        <b style={{ color: isUser ? "#1976d2" : "#7b1fa2" }}>
                          {isUser ? (m.user || "User") : "AI"}:
                        </b>{" "}
                        <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>
                        {m.timestamp && (
                          <span style={{ color: "#aaa", fontSize: 10 }}>
                            {"  ·  " + new Date(m.timestamp).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ textAlign: "center", fontSize: 11, color: "#999", margin: "8px 0 2px" }}>— current conversation —</div>
                </div>
              )}
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id}>
              <div style={messageStyle(message.type === 'user_question')}>
                {message.content}
              </div>

              {/* Examples under the welcome message — free-text chat only */}
              {message.id === 0 && (
                <div style={hintBoxStyle}>
                  <div>• Explain a change — <i>"Why did the estimate drop from $8,200 to $7,200?"</i></div>
                  <div>• Dig into a comp — <i>"Tell me more about the 177 9th Ave comp"</i></div>
                  <div>• Building facts — <i>"What are the taxes and co-op fees?"</i></div>
                  <div>• Unit history — <i>"What did this unit rent for before?"</i></div>
                  <div>• Explore comps — <i>"Show comps if we relax the similarity filter"</i></div>
                  <div>• What-if — <i>"How much would a renovation add?"</i></div>
                  <div>• Give feedback — <i>"The rent should be $8,800, this unit has a private roof deck"</i></div>
                  <div>• Take action — <i>"Re-run the comps" · "Exclude that comp"</i></div>
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div style={messageStyle(false)}>
              <div style={loadingDotsStyle}>
                <div style={dotStyle} className="chat-dot"></div>
                <div style={dotStyle} className="chat-dot"></div>
                <div style={dotStyle} className="chat-dot"></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Container */}
        <div style={inputContainerStyle}>
          <textarea
            style={inputStyle}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about this rent prediction..."
            disabled={isLoading || !sessionId}
            rows={2}
          />
          <button
            style={sendButtonStyle}
            onClick={sendMessage}
            disabled={isLoading || !sessionId || !inputMessage.trim()}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#333';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#000';
            }}
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatComponent;